<script lang="ts" setup>
import DirectSalesForm from '@/domains/direct-sales/components/direct-sales-form/direct-sales-form.vue'
import { EUiFormColor } from '@/domains/direct-sales/const/form.const'
import { Product } from '@/domains/direct-sales/const/product.const'
import { DIRECT_SALES_UTMS } from '@/domains/direct-sales/const/utm.const'

import { useConsts } from './useConsts'
import type { photoData } from '@/domains/wellbeing/types/wellbeing.types'
import type { IBoardMember } from '~/components/TeamMembers/TeamMembers/TeamMembers.types'
import UiPhotoWithDescription from '~/components/UiPhotoWithDescription/UiPhotoWithDescription.vue'
import TeamMembers from '@/components/TeamMembers/TeamMembers/TeamMembers.vue'
import { adaptNumbers } from '@/domains/home/composables/useAdaptNumbers'
import { useWellbeingPage } from '@/domains/wellbeing/composables/useWellbeingPage'

const { t, locale } = useI18n()
const { whatIs, benefits, wellbeingsNumbers } = useConsts()

const { content } = await useWellbeingPage()

const data = computed(() => {
  return {
    content: content.value,
  }
})

const uiPhotoData = (photoData: photoData) => {
  return {
    headerIcon: [{
      url: photoData.headerIcon,
    }],
    desktopHeaderClass: 'h4',
    mobileHeaderClass: 'h8',
    headingType: 'h3',
    headerTitle: photoData.headerTitle,
    desktopPhotoPosition: photoData.desktopPhotoPosition,
    photoDesktop: [{
      url: photoData.photoDesktop.url,
      public_id: photoData.photoDesktop.public_id,
    }],
    photoMobile: [{
      url: photoData.photoMobile.url,
      public_id: photoData.photoMobile.public_id,
    }],
    photoSide: photoData.photoSide,
    descriptionSize: 'small',
    description: {
      content: [
        {
          nodeType: 'paragraph',
          content: [
            {
              value: photoData.description,
            },
          ],
        },
      ],
    },
  }
}

const teamMembersData = () => {
  const members = [] as IBoardMember[]

  for (let i = 0; i < 9; i++) {
    members.push({
      boardMemberPhoto: `/images/wellbeing/photos/experts/${i + 1}.png`,
      boardMemberName: t(`wellbeing.experts.experts.expertName${i + 1}`),
      boardMemberRole: t(`wellbeing.experts.experts.expertShort${i + 1}`),
      boardMemberDescription: {
        content: [
          {
            content: [
              {
                value: t(`wellbeing.experts.experts.expertLong${i + 1}`),
              },
            ],
          },
        ],
      },
    })
  }

  return {
    boardTitle: t('wellbeing.experts.header'),
    boardLead: t('wellbeing.experts.subheader'),
    boardMembers: members,
    headingType: 'h4',
  }
}

const sliderData = () => {
  const items = []

  for (let i = 0; i < 6; i++) {
    items.push({
      image: `/images/wellbeing/photos/slider/${i + 1}.png`,
      title: t(`wellbeing.slider.slides.slideTitle${i + 1}`),
      description: t(`wellbeing.slider.slides.slideDesc${i + 1}`),
    })
  }

  return items
}
</script>

<template>
  <div class="wellbeing">
    <Hero
      desktop-image="/images/hero/wellbeing_d.webp"
      mobile-image="/images/hero/wellbeing_m.webp"
      :header="$t('wellbeing.hero.title')"
      :text="$t('wellbeing.hero.subtitle')"
      :button="{
        text: $t('wellbeing.hero.button'),
        to: '#formularz',
      }"
    />

    <div class="wellbeing__whatis">
      <UiPhotoWithDescription
        v-bind="uiPhotoData(whatIs)"
      />
    </div>

    <div class="wellbeing__programs">
      <div class="wellbeing__programs-header">
        <UiHeader
          :header="$t('wellbeing.programs.title')"
          header-size="h6"
          header-size-mobile="h8"
          position="center"
        />
      </div>

      <UiNumbers
        v-if="data?.content?.items.bsNumbers"
        :numbers="adaptNumbers(data?.content?.items.bsNumbers.numbers)"
        color="purple"
        type="box"
      />

      <p class="wellbeing__programs-star">
        {{ $t('wellbeing.programs.star') }}
      </p>
    </div>

    <div class="wellbeing__benefits">
      <div class="wellbeing__benefits-header">
        <UiHeader
          :header="$t('wellbeing.programs.title')"
          header-size="h4"
          header-size-mobile="h8"
          position="center"
        />
      </div>

      <UiPhotoWithDescription
        v-for="benefit in benefits"
        :key="benefit.description"
        v-bind="uiPhotoData(benefit)"
      />
    </div>

    <div class="wellbeing__team">
      <TeamMembers
        v-bind="teamMembersData()"
        modal-aria-label="teamModalWellbeing"
      />
    </div>

    <div class="wellbeing__slider">
      <CardCarousel
        :header="$t('wellbeing.slider.header')"
        :slides="sliderData()"
        color="gray"
      />
    </div>

    <div class="wellbeing__programs">
      <div class="wellbeing__programs-header">
        <UiHeader
          :header="$t('wellbeing.programs.title')"
          header-size="h6"
          header-size-mobile="h8"
          position="center"
        />
      </div>

      <UiNumbers
        :numbers="wellbeingsNumbers"
        color="purple"
        type="box"
      />
    </div>

    <!-- <div class="wellbeing__logos">
      <div class="wellbeing__logos-header">
        <UiHeader
          :header="$t('wellbeing.logos.header')"
          header-size="h9"
          header-size-mobile="h9"
          position="center"
        />
      </div>

      <div class="wellbeing__logos_container">
        <img
          class="wellbeing__logo"
          src="/images/wellbeing/logos/macrofarma.png"
          alt="macrofarma"
        >

        <img
          class="wellbeing__logo"
          src="/images/wellbeing/logos/bnpparibas.png"
          alt="bnp paribas"
        >

        <img
          class="wellbeing__logo"
          src="/images/wellbeing/logos/general.png"
          alt="generali"
        >

        <img
          class="wellbeing__logo"
          src="/images/wellbeing/logos/stanley.png"
          alt="Stanley Black and Decker"
        >

        <img
          class="wellbeing__logo"
          src="/images/wellbeing/logos/electronic.png"
          alt="LG electronics"
        >
      </div>
    </div> -->

    <ClientOnly>
      <div class="wellbeing__form">
        <DirectSalesForm
          variant="base"
          :product="Product.WELLBEING"
          :color="EUiFormColor.GRAY"
          :header="$t('wellbeing.form.header')"
          :badge="`/icons/badges/flag-4_${locale}.png`"
          :form-id="$t('home.form.id')"
          :utm="DIRECT_SALES_UTMS.wellbeingPage"
        />
      </div>
    </ClientOnly>
  </div>
</template>

<style lang="scss" scoped>
.wellbeing {
  display: flex;
  flex-direction: column;

  :deep(.wellbeing__hero) {
    @include body-1-medium
  }

  &__whatis {
    max-width: 100%;
    margin: 4rem 2.4rem 0 2.4rem;
  }

  &__programs {
   max-width: 90%;
   margin: 4rem 2.4rem 0 2.4rem;

   :deep(.numbers__item) {
      width: 23.2rem;
    }

    :deep(.numbers--box) {
      justify-content: center;
      max-width: 71rem;
      margin: 3.2rem auto;
    }
  }

  &__programs-star {
    @include body-2-medium;
    margin-top: 4rem;
    color: $text-200;
    text-align: center;
  }

  &__benefits {
    max-width: 90%;
    margin: 4rem 2.4rem 0 2.4rem;
  }

  &__slider {
    :deep(.swiper-button-next:after) {
      content: '';
    }

    :deep(.swiper-button-prev:after) {
      content: '';
    }

    :deep(.card__description) {
      display: flex;
    }

    :deep(.card__content){
      min-height: 34rem;
    }
  }

  &__team {
    margin: 4rem 2.4rem 0 2.4rem;
  }

  &__logos {
    width: 90%;
    margin: 4rem 2.4rem 0 2.4rem;
  }

  &__logo {
    margin-bottom: 4rem;
  }

  &__benefits-header {
    margin-bottom: 3.2rem;
  }

  &__form {
    margin-top: 4rem;
  }

  &__logos_container {
    display: flex;
    flex-wrap: wrap;
    justify-content: space-around;
  }

  &__logos-header {
    margin-bottom: 5.1rem;
  }
}

@media (min-width: 1100px) {
  .wellbeing {
    &__programs {

      :deep(.numbers--box) {
        max-width: 100%;
      }
    }
  }
}

@include atM {
  .wellbeing {
    :deep(.wellbeing__hero) {
      @include body-1-medium
    }

    &__whatis {
      margin-top: 12rem;

      :deep(.cf-photoWithDescription__column) {
        justify-content: center;
      }

      :deep(.cf-photoWithDescription__header) {
        display: flex;
        align-items: flex-start;
      }

      :deep(.cf-photoWithDescription__headerIcon) {
        margin-top: 1.2rem;
      }
    }

    &__programs {
      max-width: 132rem;
      margin: 12rem auto 0 auto;
    }

    &__programs-header {
      margin-bottom: 5.6rem;
    }

    &__benefits {
      max-width: 132rem;
      margin: 12rem auto 0 auto;

      :deep(.cf-photoWithDescription__column) {
        justify-content: center;
      }

      :deep(.cf-photoWithDescription__header) {
        display: flex;
        align-items: flex-start;
      }

      :deep(.cf-photoWithDescription__headerIcon) {
        margin-top: 1.2rem;
      }
    }

    &__logos {
      max-width: 132rem;
      width: 90%;
      margin: 12rem auto 0 auto;
    }

    &__logos_container {
      justify-content: space-between;
    }

    &__logo {
      margin: 0;
    }

    &__benefits-header {
      margin-bottom: 8rem;
    }

    &__form {
      margin-top: 12rem;
    }
  }
}

@include atL {
  .wellbeing {
    &__programs {
      :deep(.numbers__item) {
        width: 31.2rem;
      }
    }
  }
}
</style>

